import React, { useState } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useCampaigns } from '../hooks/useCampaigns';
import { Campaign, AdVariation } from '../models/Campaign';
import { ArrowBack, Save } from '@mui/icons-material';
import { VALID_CATEGORIES } from '../utils/standardizeCategories';

const NewCampaignForm: React.FC = () => {
    const navigate = useNavigate();
    const { createCampaign } = useCampaigns();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    // Form state
    const [formData, setFormData] = useState<Omit<Campaign, 'id'>>({
        centre_name: '',
        category: '',
        city: '',
        region: '',
        country: '',
        latitude: undefined,
        longitude: undefined,
        target_link: '',
        ad_variations: []
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSelectChange = (e: any) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCoordinateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        // Only update if value is a valid number or empty
        if (value === '' || !isNaN(parseFloat(value))) {
            setFormData(prev => ({
                ...prev,
                [name]: value === '' ? undefined : parseFloat(value)
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            // Validate required fields
            if (!formData.centre_name) {
                throw new Error('Centre name is required');
            }

            // Create the campaign
            const newCampaignId = await createCampaign(formData);
            
            if (newCampaignId) {
                // Navigate to the new campaign's detail page
                navigate(`/campaign/${newCampaignId}`);
            } else {
                throw new Error('Failed to create campaign');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Box sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate(-1)}
                    sx={{ mr: 2 }}
                >
                    Back
                </Button>
                <Typography 
                    variant="h4" 
                    component="h1" 
                    sx={{ 
                        fontWeight: 700,
                        background: 'linear-gradient(145deg, #1e40af 0%, #2563eb 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                    }}
                >
                    Create New Campaign
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <Paper sx={{ p: 3 }}>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                Basic Information
                            </Typography>
                        </Grid>

                        {/* Centre Name */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                fullWidth
                                label="Centre Name"
                                name="centre_name"
                                value={formData.centre_name}
                                onChange={handleInputChange}
                                variant="outlined"
                            />
                        </Grid>

                        {/* Category */}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Category</InputLabel>
                                <Select
                                    name="category"
                                    value={formData.category || ''}
                                    onChange={handleSelectChange}
                                    label="Category"
                                >
                                    <MenuItem value="">None</MenuItem>
                                    <MenuItem value={VALID_CATEGORIES.FRANCHISE}>{VALID_CATEGORIES.FRANCHISE}</MenuItem>
                                    <MenuItem value={VALID_CATEGORIES.CORPORATE}>{VALID_CATEGORIES.CORPORATE}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Target Link */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Target Link (URL)"
                                name="target_link"
                                value={formData.target_link}
                                onChange={handleInputChange}
                                placeholder="https://"
                                variant="outlined"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                Location Information
                            </Typography>
                        </Grid>

                        {/* City */}
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="City"
                                name="city"
                                value={formData.city}
                                onChange={handleInputChange}
                                variant="outlined"
                            />
                        </Grid>

                        {/* Region */}
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Region/State"
                                name="region"
                                value={formData.region}
                                onChange={handleInputChange}
                                variant="outlined"
                            />
                        </Grid>

                        {/* Country */}
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Country"
                                name="country"
                                value={formData.country}
                                onChange={handleInputChange}
                                variant="outlined"
                            />
                        </Grid>

                        {/* Latitude */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Latitude"
                                name="latitude"
                                value={formData.latitude === undefined ? '' : formData.latitude}
                                onChange={handleCoordinateChange}
                                variant="outlined"
                                type="number"
                                inputProps={{ step: 'any' }}
                            />
                        </Grid>

                        {/* Longitude */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Longitude"
                                name="longitude"
                                value={formData.longitude === undefined ? '' : formData.longitude}
                                onChange={handleCoordinateChange}
                                variant="outlined"
                                type="number"
                                inputProps={{ step: 'any' }}
                            />
                        </Grid>

                        {/* Submit Button */}
                        <Grid item xs={12} sx={{ mt: 3, textAlign: 'right' }}>
                            <Button 
                                type="submit"
                                variant="contained"
                                color="primary"
                                size="large"
                                startIcon={<Save />}
                                disabled={loading}
                                sx={{ minWidth: 150 }}
                            >
                                {loading ? <CircularProgress size={24} /> : 'Create Campaign'}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
        </Container>
    );
};

export default NewCampaignForm; 